class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    this.querySelector('#cart-notification-form')?.addEventListener('submit', async (event) => {
      if (this.certificationCheckoutVerified) {
        this.certificationCheckoutVerified = false;
        return;
      }
      event.preventDefault();
      const form = event.currentTarget;
      const checkout = this.querySelector('#cart-notification-form [name="checkout"]');
      if (!checkout || this.certificationCheckoutPending) return;
      if (checkout.disabled) {
        this.focusCertificationNotice();
        return;
      }
      this.certificationCheckoutPending = true;
      const requestVersion = (this.certificationCheckoutRequest || 0) + 1;
      this.certificationCheckoutRequest = requestVersion;
      checkout.disabled = true;
      try {
        const response = await fetch(`${routes.cart_url}?sections=cart-notification-product`);
        if (!response.ok) throw new Error('Cart certification state could not be refreshed.');
        const sections = await response.json();
        if (!this.isConnected || this.certificationCheckoutRequest !== requestVersion) return;
        this.updateCertificationGate(sections['cart-notification-product']);
        if (!checkout.disabled) {
          this.certificationCheckoutVerified = true;
          try {
            form.requestSubmit(checkout);
          } finally {
            this.certificationCheckoutVerified = false;
          }
        } else {
          this.focusCertificationNotice();
        }
      } catch (error) {
        if (this.isConnected && this.certificationCheckoutRequest === requestVersion) {
          this.updateCertificationGate();
          this.focusCertificationNotice();
        }
      } finally {
        if (this.certificationCheckoutRequest === requestVersion) this.certificationCheckoutPending = false;
      }
    });
  }

  open() {
    this.invalidateCertificationCheckout();
    const body = this.querySelector('.cart-notification__body');
    if (body) body.scrollTop = 0;
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.invalidateCertificationCheckout();
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.invalidateCertificationCheckout();
    this.updateCertificationGate(parsedState.sections?.['cart-notification-product']);
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  focusCertificationNotice() {
      const notice = this.querySelector('#CartNotification-Certification');
      if (notice && !notice.hidden) notice.focus();
      else this.querySelector('#cart-notification-button')?.focus();
  }

  invalidateCertificationCheckout() {
    this.certificationCheckoutRequest = (this.certificationCheckoutRequest || 0) + 1;
    this.certificationCheckoutPending = false;
    this.certificationCheckoutVerified = false;
    const checkout = this.querySelector('#cart-notification-form [name="checkout"]');
    if (checkout && this.certificationGateState) checkout.disabled = this.certificationGateState !== 'clear';
  }

  updateCertificationGate(sectionHtml) {
    const checkout = this.querySelector('#cart-notification-form [name="checkout"]');
    const notice = this.querySelector('#CartNotification-Certification');
    if (!checkout) return;

    let stateTemplate;
    if (typeof sectionHtml === 'string') {
      stateTemplate = new DOMParser()
        .parseFromString(sectionHtml, 'text/html')
        .querySelector('template[data-rc-cart-certification-state]');
    }
    const state = stateTemplate?.getAttribute('data-rc-cart-certification-state');
    const stateKnown = ['clear', 'blocked', 'empty'].includes(state);
    this.certificationGateState = stateKnown ? state : 'unknown';
    checkout.disabled = !stateKnown || state !== 'clear';

    if (notice) {
      notice.removeAttribute('role');
      if (stateKnown) {
        notice.innerHTML = stateTemplate.innerHTML;
        notice.hidden = state !== 'blocked';
      } else {
        notice.textContent = notice.dataset.refreshError;
        notice.hidden = false;
        notice.setAttribute('role', 'alert');
      }
      if (!notice.hidden) checkout.setAttribute('aria-describedby', notice.id);
      else checkout.removeAttribute('aria-describedby');
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
